<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import type { Props, Style } from './miniCharts';

const props = defineProps<Props>();
const model = defineModel<Props['modelValue']>();

const elRef = ref<HTMLElement | null>(null);

let pagesResizeObserver: ResizeObserver;

/**
 * Количество страниц
 */
const countPages = ref(1);

/**
 * Активная страница
 */
const activePage = ref(1);

/**
 * Установка количества страниц в блоке
 */
const setCountPages = () => {
	countPages.value = Math.round(elRef.value!.scrollHeight / elRef.value!.clientHeight);
};

watch(countPages, () => {
	if (countPages.value < activePage.value) {
		activePage.value = 1;
	}
});

watch(
	() => props.countCharts,
	() => setCountPages(),
	{ flush: 'post' },
);

/**
 * Расчет положения для подсказки
 */
const tooltipStyle = computed(() => {
	if (!model.value) return {};

	const box = model.value.elCol.getBoundingClientRect();

	const style: Style = {
		top: box.bottom + window.scrollY + 'px',
	};

	if (document.documentElement.clientWidth / 2 > box.right) {
		style.left = box.right + window.scrollX + 10 + 'px';
	} else {
		style.left = box.left + window.scrollX - 10 + 'px';
		style.transform = 'translate(-100%, 0)';
	}

	return style;
});

onMounted(() => {
	/**
	 * Функционал переключения экранов графиков
	 */
	pagesResizeObserver = new ResizeObserver(() => setCountPages());
	pagesResizeObserver.observe(elRef.value!);

	watch(activePage, () => {
		const elMiniChart = elRef.value!.children[0];
		const pageHeight = elMiniChart.clientHeight + parseInt(window.getComputedStyle(elRef.value!).getPropertyValue('row-gap'));
		const scrollTop = pageHeight * (activePage.value - 1);

		elRef.value!.scrollTo({ top: scrollTop, behavior: 'smooth' });
	});
});

onUnmounted(() => {
	pagesResizeObserver.disconnect();
});
</script>

<template>
	<div
		class="top-miniCharts"
		:class="{
			['top-miniCharts']: true,
			['scrollable']: countPages > 1,
			['top-miniCharts_hideChartInMobile']: hideChartInMobile,
		}"
		ref="elRef"
	>

		<slot></slot>

		<div
			v-if="countPages > 1"
			class="top-miniCharts_pages"
		>
			<span
				v-for="n in countPages"
				:class="{['top-active']: n === activePage}"
				@click="activePage = n"
			></span>
		</div>

		<Teleport to="body">
			<div
				v-if="model"
				class="top-miniCharts_tooltip"
				:style="tooltipStyle"
			>
				<span>{{ model.date }}</span>
				<span>{{ model.value }}</span>
			</div>
		</Teleport>
	</div>

</template>

<style>
@import "./styles/miniCharts.css";
</style>
